import React from 'react';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import useIsMobile from '@hooks/useIsMobile';

type Props = {
  open: boolean;
  onClose: () => void;
  images: string[];
  startIndex?: number;
};

const ImageLightBox = ({ open, onClose, images, startIndex = 0 }: Props) => {
  const [currentIndex, setCurrentIndex] = React.useState(startIndex);
  const isMobile = useIsMobile();
  const touchStartRef = React.useRef<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartRef.current === null) return;
    const touchEnd = e.changedTouches[0].clientX;
    const delta = touchStartRef.current - touchEnd;

    if (delta > 50) handleNext(); // swipe left
    else if (delta < -50) handlePrev(); // swipe right

    touchStartRef.current = null;
  };

  React.useEffect(() => {
    setCurrentIndex(startIndex);
  }, [startIndex, open]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'relative',
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          zIndex: 1300,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            backgroundColor: 'transparent',
            padding: 1,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon fontSize="large" sx={{ color: '#fff' }} />
        </IconButton>

        {/* DESKTOP View */}
        {!isMobile && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              width: '90%',
              maxWidth: '1000px',
            }}
          >
            <IconButton
              onClick={handlePrev}
              sx={{
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <ArrowBackIosNewIcon fontSize="large" sx={{ color: '#fff' }} />
            </IconButton>

            <img
              src={images[currentIndex]}
              alt={`Image ${currentIndex + 1}`}
              style={{
                maxHeight: '80vh',
                width: '40vw',
                borderRadius: '8px',
                objectFit: 'contain',
                transition: 'all 0.3s ease-in-out',
              }}
            />

            <IconButton
              onClick={handleNext}
              sx={{
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <ArrowForwardIosIcon fontSize="large" sx={{ color: '#fff' }} />
            </IconButton>
          </div>
        )}

        {/* MOBILE View */}
        {isMobile && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div
              style={{
                width: '95vw',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <div
                ref={containerRef}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                style={{
                  display: 'flex',
                  transition: 'transform 0.3s ease-in-out',
                  transform: `translateX(-${currentIndex * 95}vw)`,
                  width: `${images.length * 95}vw`,
                }}
              >
                {images.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`Image ${index + 1}`}
                    style={{
                      width: '95vw',
                      objectFit: 'fill',
                      flexShrink: 0,
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Pagination Dots */}
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ mt: '8px' }}
            >
              {images.map((_, index) => (
                <Box
                  key={index}
                  component="div"
                  sx={{
                    width: 10,
                    height: 10,
                    minWidth: 10,
                    minHeight: 10,
                    borderRadius: '50%',
                    backgroundColor: index === currentIndex ? '#fff' : '#888',
                    transition: 'background-color 0.2s',
                  }}
                />
              ))}
            </Box>
          </div>
        )}
      </Box>
    </Modal>
  );
};

export default ImageLightBox;